import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useMemo, useState } from "react";
import ProductCard from "@/components/products/ProductCard";
import { GIFT_FEED_PAGE_SIZE, GIFT_TYPE_TABS } from "@/constants/redemption";
import { useRedemptionProducts } from "@/hooks/useRedemptionProducts";
import type { Product } from "@/interfaces/product";
import { resolveCountryFeed } from "@/utils/product-helper";
import {
  availableCauses,
  availableGiftCategories,
  giftTypeFromCategory,
  hasAnyGiftCategory,
  matchesGiftFilters,
  supportsAnyCause,
} from "@/utils/redemption-helper";
import RedemptionFilterBar from "./RedemptionFilterBar";
import RedemptionFilterChip from "./RedemptionFilterChip";
import RedemptionFilterSheet from "./RedemptionFilterSheet";

interface RedemptionCollectionProps {
  /** Gift-link token — resolves the campaign's gift set (see useRedemptionProducts).
   *  Provide EITHER token (recipient flow) OR products (campaign-builder preview). */
  token?: string;
  /** Pre-resolved product set — the campaign-builder landing preview passes the gifts
   *  chosen in step 1 (context `savedGifts`). When present, the token fetch is skipped. */
  products?: Product[];
  /** Card tap → bubble the product id up so Redemption opens the detail slide-in. */
  onOpenProduct?: (productId: string) => void;
  /** Destination country (from the header's "Shipping to" picker) — filters the feed by
   *  product shippability. */
  activeCountry: string;
  /** Mobile layout. A prop, not a `md:` variant — the builder renders this feed inside a
   *  ~390px preview frame, where viewport-based variants would still resolve to desktop. */
  isMobile: boolean;
}

/**
 * Recipient gift collection (RFW-4) — the filterable product grid below the landing hero.
 * Owns the filter values (type / category / cause / favorites) + pagination + favorites;
 * the bar owns its own open/scroll UI state. A card tap bubbles the product id up via
 * `onOpenProduct` (the detail slide-in is a screen-covering overlay owned by Redemption).
 * Ported from the prototype's GiftCollection feed + filter chain to our components
 * (ProductCard with a favorite heart) + Tailwind, over the real campaign-scoped feed.
 */
export default function RedemptionCollection({
  token,
  products: productsProp,
  onOpenProduct,
  activeCountry,
  isMobile,
}: RedemptionCollectionProps) {
  // Recipient flow resolves the gift set from the token — the feed is RAW nested collections +
  // per-country edits, composed/filtered/edited for the active country by the SAME util the
  // builder uses. The builder preview passes an already-resolved product list directly.
  const query = useRedemptionProducts(token);
  const feed = query.data;
  const products = useMemo(() => {
    if (productsProp) return productsProp;
    if (!feed) return [];
    return resolveCountryFeed(
      feed.collections,
      activeCountry,
      feed.feedEdits[activeCountry],
      feed.addedProducts,
    );
  }, [productsProp, feed, activeCountry]);
  const isLoading = productsProp ? false : query.isLoading;
  const isError = productsProp ? false : query.isError;

  const [activeType, setActiveType] = useState("all");
  // Category / Cause are MULTI-select: a list of picks, empty = "all".
  const [activeCats, setActiveCats] = useState<string[]>([]);
  const [activeCauses, setActiveCauses] = useState<string[]>([]);
  const [favOnly, setFavOnly] = useState(false);
  const [visible, setVisible] = useState(GIFT_FEED_PAGE_SIZE);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  // The mobile filter sheet is a screen-covering modal, so it's owned here (not in
  // the bar) and rendered as a top-level sibling, like the modals in Redemption.
  const [sheetOpen, setSheetOpen] = useState(false);
  // The builder's device toggle reconciles rather than remounts, so a sheet opened on
  // mobile would otherwise stay "open" in state and reappear on switching back.
  useEffect(() => {
    if (!isMobile) setSheetOpen(false);
  }, [isMobile]);

  // The Category / Cause dropdowns offer the values of the ACTIVE TYPE TAB only. Deriving
  // them from the whole feed offered categories and causes that belong to another tab, so
  // picking one either jumped the tab or emptied the grid.
  const typeScoped = useMemo(
    () =>
      activeType === "all"
        ? products
        : products.filter((p) => giftTypeFromCategory(p.category) === activeType),
    [products, activeType],
  );
  const categories = useMemo(() => availableGiftCategories(typeScoped), [typeScoped]);
  const causes = useMemo(() => availableCauses(typeScoped), [typeScoped]);
  const filtered = useMemo(
    () =>
      products.filter((p) =>
        matchesGiftFilters(p, {
          activeType,
          activeCats,
          activeCauses,
          activeCountry,
          favOnly,
          favorites,
        }),
      ),
    [products, activeType, activeCats, activeCauses, activeCountry, favOnly, favorites],
  );
  const shown = filtered.slice(0, visible);

  const chooseType = (value: string) => {
    setActiveType(value);
    // Drop any picks the new tab has no gift for, so multi-select never lands on an empty grid.
    // (Category/Cause span types; the tab is a coarse single-select layered on top.)
    if (value !== "all") {
      const nextTypeProducts = products.filter((p) => giftTypeFromCategory(p.category) === value);
      setActiveCats((prev) =>
        prev.filter((c) => nextTypeProducts.some((p) => hasAnyGiftCategory(p, [c]))),
      );
      setActiveCauses((prev) =>
        prev.filter((cause) => nextTypeProducts.some((p) => supportsAnyCause(p, [cause]))),
      );
    }
    setVisible(GIFT_FEED_PAGE_SIZE);
  };
  // Multi-select toggles: add the pick if new, remove it if already on (empty list = "all").
  const chooseCat = (value: string) => {
    setActiveCats((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value],
    );
    setVisible(GIFT_FEED_PAGE_SIZE);
  };
  const chooseCause = (value: string) => {
    setActiveCauses((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value],
    );
    setVisible(GIFT_FEED_PAGE_SIZE);
  };
  const clearCats = () => {
    setActiveCats([]);
    setVisible(GIFT_FEED_PAGE_SIZE);
  };
  const clearCauses = () => {
    setActiveCauses([]);
    setVisible(GIFT_FEED_PAGE_SIZE);
  };
  const toggleFav = () => {
    setFavOnly((v) => !v);
    setVisible(GIFT_FEED_PAGE_SIZE);
  };
  const clearFilters = () => {
    setActiveCats([]);
    setActiveCauses([]);
    setFavOnly(false);
    setVisible(GIFT_FEED_PAGE_SIZE);
  };
  const toggleFavorite = (id: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasChips = activeCats.length > 0 || activeCauses.length > 0 || favOnly;

  return (
    <div className="bg-background pb-10">
      <RedemptionFilterBar
        isMobile={isMobile}
        typeTabs={GIFT_TYPE_TABS}
        activeType={activeType}
        onChooseType={chooseType}
        categories={categories}
        activeCats={activeCats}
        onChooseCat={chooseCat}
        onClearCats={clearCats}
        causes={causes}
        activeCauses={activeCauses}
        onChooseCause={chooseCause}
        onClearCauses={clearCauses}
        favOnly={favOnly}
        onToggleFav={toggleFav}
        onOpenSheet={() => setSheetOpen(true)}
      />

      {/* Active-filter chips */}
      {hasChips && (
        <div
          className={`mx-auto flex max-w-300 flex-wrap ${
            // R15: extra row gap on mobile so a wrapped chip's expanded (invisible) hit area
            // never overlaps the row above/below it.
            isMobile ? "gap-8 px-4 pt-4 pb-1" : "gap-2 px-6 pt-5 pb-2.5"
          }`}
        >
          {activeCats.map((c) => (
            <RedemptionFilterChip key={`cat-${c}`} label={c} onRemove={() => chooseCat(c)} />
          ))}
          {activeCauses.map((c) => (
            <RedemptionFilterChip key={`cause-${c}`} label={c} onRemove={() => chooseCause(c)} />
          ))}
          {favOnly && <RedemptionFilterChip label="Favorites" onRemove={toggleFav} />}
        </div>
      )}

      {/* Grid / states */}
      {isError ? (
        <p className="mx-auto max-w-300 px-6 py-10 text-center text-[14px] text-graphite">
          We couldn't load your gifts. Please refresh and try again.
        </p>
      ) : isLoading ? (
        <p className="mx-auto max-w-300 px-6 py-10 text-center text-[14px] text-graphite">
          Loading gifts…
        </p>
      ) : shown.length === 0 ? (
        <p className="mx-auto max-w-300 px-6 py-10 text-center text-[14px] text-graphite">
          {favOnly
            ? "No favorites yet — tap the heart on a gift to save it here."
            : "No gifts match the selected filters."}
        </p>
      ) : (
        <div
          className={`mx-auto grid max-w-300 ${
            isMobile ? "grid-cols-2 gap-3 px-4 pt-4 pb-5" : "grid-cols-3 gap-4 px-6 pt-4 pb-6"
          }`}
        >
          {shown.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              isFavorite={favorites.has(p.id)}
              onToggleFavorite={() => toggleFavorite(p.id)}
              onClick={() => onOpenProduct?.(p.id)}
            />
          ))}
        </div>
      )}

      {/* View more */}
      {visible < filtered.length && (
        <div className="mx-auto max-w-300 px-6 pt-3 pb-12 text-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + GIFT_FEED_PAGE_SIZE)}
            className="inline-flex cursor-pointer items-center gap-2 rounded-3xl border-2 border-rodeo-black bg-rodeo-black px-8 py-2.5 font-lato text-[14px] font-bold uppercase tracking-[0.5px] text-white transition-transform hover:scale-[1.03]"
          >
            View more gifts <ExpandMoreIcon sx={{ fontSize: 16 }} />
          </button>
          <div className="mt-2.5 font-lato text-[12px] text-graphite">
            Showing {shown.length} of {filtered.length} gifts
          </div>
        </div>
      )}

      {/* Mobile filter sheet — a screen-covering modal, owned here (opened by the
          bar's Filters tool) and rendered as a top-level sibling. */}
      {isMobile && (
        <RedemptionFilterSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          categories={categories}
          activeCats={activeCats}
          onChooseCat={chooseCat}
          onClearCats={clearCats}
          causes={causes}
          activeCauses={activeCauses}
          onChooseCause={chooseCause}
          onClearCauses={clearCauses}
          favOnly={favOnly}
          onToggleFav={toggleFav}
          onClearFilters={clearFilters}
          filteredCount={filtered.length}
        />
      )}
    </div>
  );
}
